// ExitIntentPopup.jsx — Boston Iron Grit
// Drop into frontend/src/components/ and render <ExitIntentPopup /> once in App.js (inside the Router).
// Sends each lead to BOTH:
//   1) the site's existing POST /api/leads  (MongoDB + /admin lead table)
//   2) Mailchimp "BIG Community" audience via the public signup endpoint (no API key needed)
// Mailchimp is set to single opt-in, and new-subscriber alerts go instantly to tina@crossfitbostonirongrit.com.
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const MC_URL =
  "https://crossfitirongrit.us5.list-manage.com/subscribe/post-json?u=7bd62378296373ff10c88d3a5&id=14e9e65228&f_id=00bf43edf0";
const MC_HONEYPOT = "b_7bd62378296373ff10c88d3a5_14e9e65228";
const SNOOZE_KEY = "big_exit_popup_until";
const SNOOZE_DAYS = 7;
const SKIP_PATHS = ["/admin", "/confirm", "/unsubscribe", "/book"];
const INTERESTS = [
  "CrossFit classes",
  "HYROX training",
  "Peptides / GLP-1s & Live B.I.G. 365",
  "Personal training / Gold Standard",
  "Olympic lifting / Powerlifting",
  "Not sure yet",
];
const GOALS = [
  "Get stronger",
  "Lose body fat",
  "Train for a race",
  "Longevity / feel better at 40+ / 50+",
  "Get back into a routine",
];
const EXPERIENCE = ["Brand new to this", "Trained before, been away", "Currently training"];
const BEST_TIME = ["Morning", "Midday", "Evening"];
const CONTACT = ["Text", "Call", "Email"];
const utm = (k) => {
  try { return sessionStorage.getItem(`big_${k}`) || ""; } catch { return ""; }
};
const snoozed = () => {
  try { return Number(localStorage.getItem(SNOOZE_KEY) || 0) > Date.now(); } catch { return false; }
};
const snooze = (days = SNOOZE_DAYS) => {
  try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + days * 864e5)); } catch {}
};
// Mailchimp only accepts JSONP from the browser
function mailchimpJsonp(fields) {
  return new Promise((resolve) => {
    const cb = `mc_cb_${Date.now()}`;
    const qs = new URLSearchParams({ ...fields, [MC_HONEYPOT]: "" }).toString();
    const s = document.createElement("script");
    const done = (r) => { try { delete window[cb]; s.remove(); } catch {} resolve(r); };
    window[cb] = (r) => done(r);
    s.onerror = () => done({ result: "error" });
    s.src = `${MC_URL}&${qs}&c=${cb}`;
    document.body.appendChild(s);
    setTimeout(() => done({ result: "timeout" }), 8000);
  });
}
export default function ExitIntentPopup() {
  const { pathname } = useLocation();
  const onSkipPath = SKIP_PATHS.some((p) => pathname.startsWith(p));
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const openedAt = useRef(Date.now());
  const [f, setF] = useState({
    firstName: "", email: "", phone: "", programInterest: "", primaryGoal: "",
    experienceLevel: "", preferredTime: "", preferredContactMethod: "Text",
    marketingConsent: true, hp: "",
  });
  const set = (k) => (e) =>
    setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  useEffect(() => {
    if (onSkipPath) {
      setOpen(false);
      return;
    }
    if (snoozed()) return;
    let fired = false;
    const show = () => {
      if (fired || snoozed()) return;
      fired = true;
      openedAt.current = Date.now();
      setOpen(true);
      if (window.gtag) window.gtag("event", "exit_popup_view");
    };
    // Desktop: cursor leaves toward the tab bar
    const onOut = (e) => { if (!e.relatedTarget && e.clientY <= 8) show(); };
    // Mobile: fast upward scroll after reading, or 45s on page
    let lastY = window.scrollY, lastT = Date.now();
    const onScroll = () => {
      const y = window.scrollY, t = Date.now();
      if (y > 600 && lastY - y > 250 && t - lastT < 400) show();
      lastY = y; lastT = t;
    };
    const timer = setTimeout(() => { if (window.innerWidth < 768) show(); }, 45000);
    // Any other lead form submitted on the site: don't pester
    const onSubmit = (e) => { if (e.target?.dataset?.testid === "lead-form") snooze(30); };
    document.addEventListener("mouseout", onOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("submit", onSubmit, true);
    return () => {
      document.removeEventListener("mouseout", onOut);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("submit", onSubmit, true);
      clearTimeout(timer);
    };
  }, [onSkipPath]);
  const close = () => { snooze(); setOpen(false); };
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.firstName || !/^\S+@\S+\.\S+$/.test(f.email) || !f.programInterest) {
      setError("Add your first name, a valid email and what you're interested in.");
      return;
    }
    if (f.hp) return; // bot
    setSending(true);
    const source = utm("utm_source") || (document.referrer ? "referral" : "direct");
    const message = `Exit popup · Interest: ${f.programInterest} · Best time: ${f.preferredTime || "any"}`;
    try {
      // 1) Site DB (same payload shape as the existing lead form)
      await axios.post(`${API}/leads`, {
        firstName: f.firstName, lastName: "", email: f.email, phone: f.phone,
        primaryGoal: f.primaryGoal, experienceLevel: f.experienceLevel,
        preferredContactMethod: f.preferredContactMethod, preferredTime: f.preferredTime,
        message, marketingConsent: f.marketingConsent, hp: "",
        ts: openedAt.current, formName: "exit-intent-popup", programInterest: f.programInterest,
        utmSource: utm("utm_source"), utmMedium: utm("utm_medium"), utmCampaign: utm("utm_campaign"),
        utmContent: utm("utm_content"), utmTerm: utm("utm_term"), gclid: utm("gclid"),
        pageUrl: window.location.href, submittedAt: new Date().toISOString(),
      });
    } catch (err) {
      // keep going — Mailchimp still captures the lead
      console.warn("lead api failed", err);
    }
    // 2) Mailchimp (Tina gets the instant new-subscriber email)
    const digits = (f.phone || "").replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
    const phoneFields =
      digits.length === 10
        ? { "PHONE[area]": digits.slice(0, 3), "PHONE[detail1]": digits.slice(3, 6), "PHONE[detail2]": digits.slice(6) }
        : {};
    await mailchimpJsonp({
      EMAIL: f.email, FNAME: f.firstName, ...phoneFields,
      INTEREST: f.programInterest, GOAL: f.primaryGoal, EXPERIENCE: f.experienceLevel,
      CONTACTVIA: f.preferredContactMethod, BESTTIME: f.preferredTime,
      LEADSRC: `exit-popup / ${source}`, LEADMSG: window.location.pathname,
    });
    if (window.gtag) {
      window.gtag("event", "generate_lead", {
        form_name: "exit-intent-popup", program_interest: f.programInterest,
        page_location: window.location.href,
      });
    }
    snooze(90);
    setSending(false);
    setDone(true);
  };
  if (!open) return null;
  const field = "w-full bg-[#0b0b0c] border border-white/15 text-white px-3 py-2.5 text-sm focus:outline-none focus:border-[#9D0423]";
  const label = "block text-[11px] tracking-[0.18em] uppercase text-white/60 mb-1.5";
  return (
    <div role="dialog" aria-modal="true" aria-labelledby="exit-popup-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4"
      onClick={(e) => e.target === e.currentTarget && close()} data-testid="exit-popup">
      <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto bg-[#121214] border border-white/10 p-6 sm:p-8">
        <button onClick={close} aria-label="Close" className="absolute top-3 right-4 text-white/60 hover:text-white text-2xl">×</button>
        {done ? (
          <div className="text-center py-6">
            <h3 className="font-heading font-extrabold uppercase text-2xl text-white">You're on Tina's list.</h3>
            <p className="mt-3 text-white/70">A coach will reach out, usually the same day. Want to talk now? Call or text <a className="text-white underline" href="tel:+16176378900">617-637-8900</a>.</p>
            <button onClick={() => setOpen(false)} className="mt-6 bg-[#9D0423] text-white px-6 py-3 uppercase tracking-widest text-xs font-bold">Keep browsing</button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="space-y-4">
            <p className="text-[11px] tracking-[0.22em] uppercase text-[#9D0423] font-bold">// Before you go</p>
            <h3 id="exit-popup-title" className="font-heading font-extrabold uppercase text-2xl sm:text-3xl text-white leading-tight">
              Free goal review.<br />No pressure.
            </h3>
            <p className="text-white/70 text-sm">Tell us what you're after and Tina or a coach will reach out with the right first step.</p>
            <input type="text" value={f.hp} onChange={set("hp")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" name="company" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className={label} htmlFor="xp-fn">First name *</label>
                <input id="xp-fn" className={field} value={f.firstName} onChange={set("firstName")} autoComplete="given-name" /></div>
              <div><label className={label} htmlFor="xp-ph">Phone</label>
                <input id="xp-ph" type="tel" className={field} value={f.phone} onChange={set("phone")} autoComplete="tel" /></div>
            </div>
            <div><label className={label} htmlFor="xp-em">Email *</label>
              <input id="xp-em" type="email" className={field} value={f.email} onChange={set("email")} autoComplete="email" /></div>
            <div><label className={label} htmlFor="xp-in">What are you most interested in? *</label>
              <select id="xp-in" className={field} value={f.programInterest} onChange={set("programInterest")}>
                <option value="">Choose one…</option>{INTERESTS.map((o) => <option key={o}>{o}</option>)}
              </select></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className={label} htmlFor="xp-go">Main goal</label>
                <select id="xp-go" className={field} value={f.primaryGoal} onChange={set("primaryGoal")}>
                  <option value="">Choose one…</option>{GOALS.map((o) => <option key={o}>{o}</option>)}
                </select></div>
              <div><label className={label} htmlFor="xp-ex">Experience</label>
                <select id="xp-ex" className={field} value={f.experienceLevel} onChange={set("experienceLevel")}>
                  <option value="">Choose one…</option>{EXPERIENCE.map((o) => <option key={o}>{o}</option>)}
                </select></div>
              <div><label className={label} htmlFor="xp-bt">Best time to reach you</label>
                <select id="xp-bt" className={field} value={f.preferredTime} onChange={set("preferredTime")}>
                  <option value="">Any time</option>{BEST_TIME.map((o) => <option key={o}>{o}</option>)}
                </select></div>
              <div><label className={label} htmlFor="xp-cv">How should we reach you?</label>
                <select id="xp-cv" className={field} value={f.preferredContactMethod} onChange={set("preferredContactMethod")}>
                  {CONTACT.map((o) => <option key={o}>{o}</option>)}
                </select></div>
            </div>
            <label className="flex items-start gap-2 text-xs text-white/60">
              <input type="checkbox" checked={f.marketingConsent} onChange={set("marketingConsent")} className="mt-0.5" />
              Also send me occasional training tips and gym updates. No spam.
            </label>
            {error && <p className="text-sm text-red-400" role="alert">{error}</p>}
            <button type="submit" disabled={sending}
              className="w-full bg-[#9D0423] hover:bg-[#b3052a] disabled:opacity-60 text-white py-3.5 uppercase tracking-widest text-xs font-bold">
              {sending ? "Sending…" : "Book my free goal review"}
            </button>
            <p className="text-[11px] text-white/40 text-center">By submitting, you agree to be contacted by Boston Iron Grit about your inquiry. We never share your info.</p>
          </form>
        )}
      </div>
    </div>
  );
}
